import React, { useEffect, useMemo, useRef, useState } from "react";

export default function DemoSpotlightOverlay({
  targetId,
  enabled,
  refreshTick = 0,
  followScroll = true,
}) {
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const activeTargetRef = useRef(null);
  const rafRef = useRef(null);
  const offscreenScrollRafRef = useRef(null);
  const offscreenScrollPendingRef = useRef(false);
  const rectRef = useRef(null);
  const scrollContainersRef = useRef([]);
  const viewportRef = useRef({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (!enabled || !targetId) {
      setRect(null);
      rectRef.current = null;
      return;
    }

    function isOffscreen(r) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      return (
        r.bottom <= 0 ||
        r.top >= viewportHeight ||
        r.right <= 0 ||
        r.left >= viewportWidth
      );
    }

    function getScrollContainers(el) {
      const containers = [];
      let node = el?.parentElement || null;
      while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        const isScrollableY =
          (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
          node.scrollHeight > node.clientHeight;
        const isScrollableX =
          (overflowX === "auto" || overflowX === "scroll" || overflowX === "overlay") &&
          node.scrollWidth > node.clientWidth;
        if (isScrollableY || isScrollableX) containers.push(node);
        node = node.parentElement;
      }
      return containers;
    }

    function scheduleRectUpdate() {
      if (rafRef.current !== null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        updateRectNow();
      });
    }

    function scheduleFollowupAfterScrollIntoView() {
      if (offscreenScrollRafRef.current !== null) return;
      offscreenScrollRafRef.current = window.requestAnimationFrame(() => {
        offscreenScrollRafRef.current = null;
        offscreenScrollPendingRef.current = false;
        scheduleRectUpdate();
      });
    }

    function updateRectNow() {
      const el = document.querySelector(`[data-demo-target="${targetId}"]`);
      if (!el) {
        if (rectRef.current !== null) {
          rectRef.current = null;
          setRect(null);
        }
        return;
      }
      let r = el.getBoundingClientRect();
      scrollContainersRef.current = getScrollContainers(el);
      if (isOffscreen(r) && !offscreenScrollPendingRef.current) {
        offscreenScrollPendingRef.current = true;
        el.scrollIntoView({
          behavior: "auto",
          block: "center",
          inline: "nearest",
        });
        scheduleFollowupAfterScrollIntoView();
        return;
      }

      r = el.getBoundingClientRect();
      const pad = 8;
      const nextRect = {
        top: Math.round(r.top - pad),
        left: Math.round(r.left - pad),
        width: Math.round(r.width + pad * 2),
        height: Math.round(r.height + pad * 2),
      };
      const nextViewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      const prevRect = rectRef.current;
      const rectChanged =
        !prevRect ||
        prevRect.top !== nextRect.top ||
        prevRect.left !== nextRect.left ||
        prevRect.width !== nextRect.width ||
        prevRect.height !== nextRect.height;
      if (rectChanged) {
        rectRef.current = nextRect;
        setRect(nextRect);
      }

      const prevViewport = viewportRef.current;
      const viewportChanged =
        prevViewport.width !== nextViewport.width ||
        prevViewport.height !== nextViewport.height;
      if (viewportChanged) {
        viewportRef.current = nextViewport;
        setViewport(nextViewport);
      }
    }

    updateRectNow();
    window.addEventListener("resize", scheduleRectUpdate, { passive: true });
    if (followScroll) {
      window.addEventListener("scroll", scheduleRectUpdate, { passive: true });
      const el = document.querySelector(`[data-demo-target="${targetId}"]`);
      const containers = el ? getScrollContainers(el) : [];
      scrollContainersRef.current = containers;
      containers.forEach((container) => {
        container.addEventListener("scroll", scheduleRectUpdate, { passive: true });
      });
    }

    return () => {
      window.removeEventListener("resize", scheduleRectUpdate);
      if (followScroll) {
        window.removeEventListener("scroll", scheduleRectUpdate);
        scrollContainersRef.current.forEach((container) => {
          container.removeEventListener("scroll", scheduleRectUpdate);
        });
      }
      scrollContainersRef.current = [];
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (offscreenScrollRafRef.current !== null) {
        window.cancelAnimationFrame(offscreenScrollRafRef.current);
        offscreenScrollRafRef.current = null;
      }
      offscreenScrollPendingRef.current = false;
    };
  }, [enabled, targetId, refreshTick, followScroll]);

  useEffect(() => {
    if (activeTargetRef.current) {
      activeTargetRef.current.classList.remove("demo-spotlight-target-active");
      activeTargetRef.current = null;
    }

    if (!enabled || !targetId) return;

    const el = document.querySelector(`[data-demo-target="${targetId}"]`);
    if (!el) return;

    el.classList.add("demo-spotlight-target-active");
    activeTargetRef.current = el;

    return () => {
      if (activeTargetRef.current) {
        activeTargetRef.current.classList.remove("demo-spotlight-target-active");
        activeTargetRef.current = null;
      }
    };
  }, [enabled, targetId]);

  const spotlight = useMemo(() => {
    if (!rect) return null;
    const top = Math.max(rect.top, 0);
    const left = Math.max(rect.left, 0);
    const width = Math.max(rect.width, 0);
    const height = Math.max(rect.height, 0);
    const right = Math.max(viewport.width - (left + width), 0);
    const bottom = Math.max(viewport.height - (top + height), 0);

    return {
      hole: {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
      },
      panels: [
        { top: "0px", left: "0px", width: "100vw", height: `${top}px` },
        { top: `${top}px`, left: "0px", width: `${left}px`, height: `${height}px` },
        {
          top: `${top}px`,
          left: `${left + width}px`,
          width: `${right}px`,
          height: `${height}px`,
        },
        { top: `${top + height}px`, left: "0px", width: "100vw", height: `${bottom}px` },
      ],
    };
  }, [rect, viewport]);

  if (!enabled) return null;

  return (
    <div className="demo-spotlight-overlay" aria-hidden="true">
      {spotlight?.panels?.map((style, index) => (
        <div
          key={`demo-spotlight-panel-${index}`}
          className="demo-spotlight-overlay__panel"
          style={style}
        />
      ))}
      {spotlight?.hole ? (
        <div className="demo-spotlight-overlay__hole" style={spotlight.hole} />
      ) : null}
    </div>
  );
}
